'use client';

import React, { forwardRef, MouseEvent } from 'react';

import Link from 'next/link';

import { useAppRouter } from '@contexts/router';

type NavigateOptions =
  | string
  | {
      pathname: string;
      query?: Record<string, any>;
    };

interface RouterLinkProps
  extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  href: NavigateOptions;
  children: React.ReactNode;
}

const RouterLink = forwardRef<HTMLAnchorElement, RouterLinkProps>(
  ({ href, children, onClick, ...props }, ref) => {
    const { push } = useAppRouter();

    // Convert href to string for the anchor tag
    const getHrefString = (linkHref: NavigateOptions): string => {
      if (typeof linkHref === 'string') {
        return linkHref;
      }

      const { pathname, query } = linkHref;
      if (query) {
        const searchParams = new URLSearchParams();
        Object.entries(query).forEach(([key, val]) => {
          if (val !== null && val !== undefined) {
            searchParams.set(key, String(val));
          }
        });
        return `${pathname}?${searchParams.toString()}`;
      }

      return pathname;
    };

    // Check if link is external or special protocol
    const isExternalLink = (linkHref: string): boolean => {
      return (
        linkHref.startsWith('http://') ||
        linkHref.startsWith('https://') ||
        linkHref.startsWith('mailto:') ||
        linkHref.startsWith('tel:') ||
        linkHref.startsWith('ftp://')
      );
    };

    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
      // Call custom onClick handler if provided
      if (onClick) {
        onClick(event);
      }

      // Don't handle navigation if:
      // - Event was prevented by custom onClick
      // - Not a left click (button 0)
      // - Modifier keys are pressed (new tab/window)
      // - Link is external
      const hrefString = getHrefString(href);
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        isExternalLink(hrefString)
      ) {
        return;
      }

      // Use router.push to trigger global loader with original href
      event.preventDefault();
      push(href);
    };

    const hrefString = getHrefString(href);

    return (
      <Link
        href={hrefString}
        ref={ref}
        onClick={handleClick}
        {...props}
        prefetch={false}
      >
        {children}
      </Link>
    );
  }
);

export default RouterLink;
